// The schema the player pages type the profile tree with (spec/dashboard.md section 11.2): the environment's default pin →
// the release → its shipped schema (section 14), served to the typed widgets as a content catalog. The released schema
// types the fields; the release's source branch supplies what the release files do not carry — the templates' display
// formats and display names, the localization strings behind the names — and the documents the references resolve to.
import { useMemo, type ReactNode } from 'react';
import { ops } from '../api/operations';
import type { EnumDefinition, LocalizationSettings, LocalizationString, Product, Release, ReleasePin, ReleaseSchema, Template } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ContentContext, type ContentCatalog } from '../content/ContentContext';
import { labelText, type DocumentLike, type FormatContext } from '../content/format';
import { useDocumentResolver } from '../content/resolver';
import { useGame } from '../shell/GameContext';
import { useTimeZone } from '../ui/time';
import { useQuery } from '../workspace/cache';
import { useOptionalWorkspace, useWorkspaceVersion } from '../workspace/react';
import { enumsOfSchema, mergeTemplateMeta, templatesOfSchema } from './model';

export interface ReleaseInfo {
  /** the environment's default pin, when one is active */
  pin: ReleasePin | null;
  release: Release | null;
  schema: ReleaseSchema | null;
  templates: Map<string, Template>;
  templateList: Template[];
  enums: Map<string, EnumDefinition>;
  /** the release's source branch: where its documents, strings and display formats live */
  branch: string | null;
  /** the branch's localization strings by id, for the names the labels show */
  strings: Map<string, LocalizationString>;
  /** the branch's products by id and by SKU, for the purchase rows' names */
  products: Map<string, Product>;
  defaultLanguage: string;
  loading: boolean;
  error: unknown;
  /** the pins were read and none is the default: the tree is untyped (section 11.2) */
  noPin: boolean;
}

interface BranchMeta {
  templates: Template[];
  strings: LocalizationString[];
  products: Product[];
  defaultLanguage: string;
}

/** The pinned release of an environment with its schema and the naming metadata of its source branch. */
export function useReleaseInfo(envSlug: string): ReleaseInfo {
  const { client } = useAuth();
  const { org, game } = useGame();
  // the active pins only (`?active=true`, section 11.2: the default pin is among them whatever the history's length)
  const pinsQ = useQuery(envSlug ? `branch:${game.id}:env:${envSlug}:pins` : null, () => client.data(ops.listPins, { path: { org, game: game.slug, env: envSlug }, query: { active: 'true', limit: 50 } }));
  const pin = useMemo(() => ((pinsQ.data?.items ?? []) as ReleasePin[]).find((p) => p.isDefault && !p.revokedAt) ?? null, [pinsQ.data]);
  const releaseQ = useQuery(pin ? `game:${org}/${game.slug}:release:${pin.releaseId}` : null, () => client.data(ops.getRelease, { path: { org, game: game.slug, release: pin!.releaseId } }));
  const schemaQ = useQuery(pin ? `game:${org}/${game.slug}:release-schema:${pin.releaseId}` : null, () => client.data(ops.getReleaseSchema, { path: { org, game: game.slug, release: pin!.releaseId } }));
  const branch = (releaseQ.data as Release | undefined)?.branchName ?? null;
  // the editor metadata the release files lack: the branch's templates (display formats) and strings (names); best effort
  const metaQ = useQuery(branch ? `game:${org}/${game.slug}:release-meta:${branch}` : null, async (): Promise<BranchMeta> => {
    const path = { org, game: game.slug, branch: branch! };
    const [templates, strings, products, settings] = await Promise.all([
      client.all<Template>((cursor) => client.data(ops.listTemplates, { path, query: { limit: 200, cursor: cursor ?? undefined } })).catch(() => [] as Template[]),
      client.all<LocalizationString>((cursor) => client.data(ops.listLocalizationStrings, { path, query: { limit: 200, cursor: cursor ?? undefined } })).catch(() => [] as LocalizationString[]),
      client.all<Product>((cursor) => client.data(ops.listProducts, { path, query: { limit: 200, cursor: cursor ?? undefined } })).catch(() => [] as Product[]),
      client.data(ops.getLocalizationSettings, { path }).catch(() => null),
    ]);
    return { templates, strings, products, defaultLanguage: (settings as LocalizationSettings | null)?.defaultLanguage ?? 'en' };
  });
  const templateList = useMemo(() => mergeTemplateMeta(templatesOfSchema(schemaQ.data as ReleaseSchema | undefined), metaQ.data?.templates ?? []), [schemaQ.data, metaQ.data]);
  const templates = useMemo(() => new Map(templateList.map((t) => [t.name, t])), [templateList]);
  const enums = useMemo(() => new Map(enumsOfSchema(schemaQ.data as ReleaseSchema | undefined).map((e) => [e.name, e])), [schemaQ.data]);
  const strings = useMemo(() => new Map((metaQ.data?.strings ?? []).map((s) => [s.id, s])), [metaQ.data]);
  const products = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of metaQ.data?.products ?? []) {
      m.set(p.id, p);
      if (p.itemId && !m.has(p.itemId)) m.set(p.itemId, p);
    }
    return m;
  }, [metaQ.data]);
  const loading = pinsQ.loading || (!!pin && (releaseQ.loading || schemaQ.loading));
  return useMemo<ReleaseInfo>(
    () => ({
      pin,
      release: (releaseQ.data as Release | undefined) ?? null,
      schema: (schemaQ.data as ReleaseSchema | undefined) ?? null,
      templates,
      templateList,
      enums,
      branch,
      strings,
      products,
      defaultLanguage: metaQ.data?.defaultLanguage ?? 'en',
      loading,
      error: pinsQ.error ?? releaseQ.error ?? schemaQ.error,
      noPin: !pinsQ.loading && !pinsQ.error && !pin,
    }),
    [pin, releaseQ.data, schemaQ.data, templates, templateList, enums, branch, strings, products, metaQ.data, loading, pinsQ.loading, pinsQ.error, releaseQ.error, schemaQ.error],
  );
}

/**
 * A content catalog over the release for the typed widgets: the schema's templates and enums, the branch's strings for the
 * names (assets and products stay empty — a profile carries ids, not content); documents resolve through the branch at its head.
 */
export function ReleaseCatalogProvider({ info, children }: { info: ReleaseInfo; children: ReactNode }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const workspace = useOptionalWorkspace();
  const workspaceVersion = useWorkspaceVersion(workspace ?? undefined);
  const [zone] = useTimeZone();
  const branch = info.branch ?? '';
  const { resolve: resolveOnBranch, resolver, version } = useDocumentResolver(client, org, game.slug, branch, null, workspace);
  const resolve = useMemo(() => (branch ? resolveOnBranch : (): DocumentLike | undefined => undefined), [branch, resolveOnBranch]);
  const stringText = useMemo(
    () =>
      (id: string): string | undefined => {
        const s = info.strings.get(id);
        if (!s) return undefined;
        const values = (s.values ?? {}) as Record<string, string>;
        return values[info.defaultLanguage] ?? Object.values(values)[0] ?? s.key;
      },
    [info.strings, info.defaultLanguage],
  );
  const productLabel = useMemo(() => (id: string): string | undefined => info.products.get(id)?.name, [info.products]);
  const format = useMemo<FormatContext>(() => ({ enums: info.enums, templates: info.templates, zone, offsets, resolve, stringText, assetName: () => undefined, productLabel }), [info.enums, info.templates, zone, offsets, resolve, stringText, productLabel]);
  const value = useMemo<ContentCatalog>(
    () => ({
      branch,
      templates: info.templates,
      templateList: info.templateList,
      enums: info.enums,
      strings: info.strings,
      stringList: Array.from(info.strings.values()),
      groups: [],
      assets: new Map(),
      assetList: [],
      products: info.products,
      productList: Array.from(new Set(info.products.values())),
      tags: [],
      languages: [{ code: info.defaultLanguage, name: info.defaultLanguage }],
      defaultLanguage: info.defaultLanguage,
      loading: info.loading,
      error: info.error ? String(info.error instanceof Error ? info.error.message : info.error) : null,
      resolve,
      resolver,
      format,
      label: (doc) => labelText(doc, format),
      stringText,
      refresh: () => resolver.invalidate(),
      version: version + workspaceVersion,
    }),
    [branch, info, resolve, resolver, format, stringText, productLabel, version, workspaceVersion],
  );
  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}
